import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import { contactItems } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db7netnidngsrf4ehngg";
const SUBJECT_OPTIONS = ["内容建议", "资料纠错", "合作咨询", "其他"];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  useSeo({
    title: "关于我们 - 佐佐木明希官网",
    description:
      "了解佐佐木明希官网的站点定位、内容范围与联系方式，并通过反馈表单向我们提交建议或合作咨询。",
    keywords: "佐佐木明希官网,关于我们,联系方式,反馈",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }
    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : value.name);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "提交失败，请稍后重试或通过邮箱联系我们"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试或通过邮箱联系我们");
    }
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "首页", path: "/" }, { label: "关于我们" }]}
        title="关于我们"
        lead="「佐佐木明希官网」是一个非官方的粉丝资讯演示站点，我们以编辑式的排版与清晰的信息结构，整理佐佐木明希的影视作品与演艺动态。"
      />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站定位</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                本站聚焦日本演员佐佐木明希，为她关注者提供一站式的内容入口：从最新明星资讯、公开活动记录，到完整的影视作品清单与角色资料，我们希望用理性的信息组织方式，替代碎片化的浏览体验。
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                站内提供资讯分类浏览、作品多维筛选、关键词检索与相关推荐等功能。所有内容均为演示示例数据，不代表任何真实人物或机构的实际信息。
              </p>

              <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
              <p className="mt-2 text-xs text-foreground-500">以下联系方式与地址均为演示信息</p>
              <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {contactItems.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                      <i className={item.icon} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-label uppercase tracking-wider text-foreground-500">
                        {item.label}
                      </span>
                      <span className="mt-1 block text-sm text-foreground-900">{item.value}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <h2 className="font-heading text-lg font-bold text-foreground-950">反馈与咨询</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  如果你对本站内容有建议，或希望与我们取得联系，请填写下方表单。
                </p>

                <form
                  ref={formRef}
                  id="about-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 space-y-5"
                >
                  <div>
                    <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入姓名或昵称"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      电子邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="fb-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      反馈类型
                    </label>
                    <select
                      id="fb-subject"
                      name="subject"
                      defaultValue={SUBJECT_OPTIONS[0]}
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    >
                      {SUBJECT_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="fb-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="请描述你的建议或问题（最多 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                    />
                    <p className="mt-1.5 text-right text-xs text-foreground-400">{charCount} / 500</p>
                  </div>

                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-guard-field"
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                        正在提交...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" aria-hidden="true" />
                        提交反馈
                      </>
                    )}
                  </button>

                  {status === "success" && (
                    <div
                      role="status"
                      className="flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800"
                    >
                      <i className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-600" aria-hidden="true" />
                      <span>感谢你的反馈，我们已经收到你的留言，会尽快回复你。</span>
                    </div>
                  )}

                  {status === "error" && (
                    <div
                      role="alert"
                      className="flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 p-4 text-sm text-accent-900"
                    >
                      <i className="ri-error-warning-line mt-0.5 text-lg text-accent-700" aria-hidden="true" />
                      <span>{formError}</span>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">版权与免责声明</h2>
          <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-800">
                <i className="ri-copyright-line" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">版权说明</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本站为演示性质的粉丝资讯站，所有文字、作品与图片均为示例内容，不涉及任何真实版权资源。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-800">
                <i className="ri-shield-cross-line" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">免责声明</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                页面中的人物资料、奖项与联络方式均为虚构示例，与任何真实人物或机构无关，请勿作为真实信息引用。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-800">
                <i className="ri-link-m" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">内容来源</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本站暂未接入任何真实数据接口，资讯与作品信息均由演示数据构成，仅用于展示网站结构与交互。
              </p>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              查看明星资讯
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}